<script setup lang="ts">
import type { LinkFragment, NavigationLinkFragment } from '#graphql-operations'

interface ButtonNavProps {
  title?: string
  active?: boolean
  link?: NavigationLinkFragment | LinkFragment
}

const props = defineProps<ButtonNavProps>()

const isCraftLink = computed(() => props.link?.__typename === 'LinkData')
const isNavigationLink = computed(() => props.link?.__typename?.endsWith('_FreeNavNode'))

const craftLink = computed(() => isCraftLink.value ? parseCraftLink(props.link) : null)
</script>

<template>
  <CraftLink
    v-if="link && isCraftLink"
    :link="(link as LinkFragment)"
    class="buttonNav typo-sans-1 inline-block uppercase"
    :class="{ active }"
  >
    <Text :text="craftLink?.title" />
    <slot name="icon" />
  </CraftLink>
  <Anchor
    v-else-if="link && isNavigationLink"
    :to="link.url"
    :external="'newWindow' in link && !!link.newWindow"
    class="buttonNav"
    :class="{ active }"
  >
    <slot>
      <Text :text="title || link.title" class="typo-sans-1 inline-block uppercase" />
    </slot>
    <slot name="icon" />
  </Anchor>
  <button
    v-else
    type="button"
    class="buttonNav"
  >
    <slot>
      <Text :text="title" class="typo-sans-1 inline-block uppercase" />
    </slot>
    <slot name="icon" />
  </button>
</template>

<style scoped>
.buttonNav {
  position: relative;
  display: inline-flex;
  align-items: center;
  /* gap: calc(var(--spacing) * 1); */

  transition: color 0.32s var(--ease-custom-circ);

  &:not([disabled]) {
    cursor: pointer;
  }

  &:deep(.icon) {
    position: absolute;
    left: 100%;
    margin-left: calc(var(--spacing) * 2);
  }
}

.buttonNav::after {
  content: '';
  bottom: 0;
  left: 0;
  height: 1px;
  width: 100%;
  position: absolute;
  background-color: currentColor;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.32s var(--ease-custom-circ);
}

@media (hover: hover) and (pointer: fine) {
  .buttonNav:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }

  .buttonNav:focus-within::after {
    transform: scaleX(1);
    transform-origin: left;
  }
}

.buttonNav.router-link-exact-active,
.buttonNav.active {
  color: var(--accent-color);

  &::after {
    transform: scaleX(1);
    transform-origin: left;
    transition: none;
  }
}
</style>
